import React, { Component, ErrorInfo, ReactNode } from "react";
import PaneStatus from "../feedback/PaneStatus";
import { Button } from "../ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * The pane's last line of defence.
 *
 * It sits OUTSIDE the app's providers (see app/Providers.tsx) so that a failure in
 * the store, the auth provider or the host adapter still reaches it. It needs nothing
 * from them to look right: the colours are CSS variables on the page (styles/), not a
 * theme object in React context, so the fallback wears the product's colours -- in
 * whichever scheme was last applied to <html> -- with no provider above it.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const detail = this.state.error?.message;

      return (
        <div className="flex h-full bg-background">
          <PaneStatus
            fill
            tone="error"
            title="Something went wrong"
            description={
              <>
                The add-in stopped unexpectedly. Reloading the pane usually clears it; your document
                has not been changed.
                {detail && (
                  <div className="mt-2 text-caption break-words text-disabled">{detail}</div>
                )}
              </>
            }
            action={
              <Button size="lg" onClick={() => window.location.reload()}>
                Reload the pane
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
